import type { ReactNode, Ref } from "react";

import { PageTitle } from "./PageTitle";
import styles from "./ScreenHead.module.css";

interface ScreenHeadProps {
  /**
   * The micro-label above the title — „Meine Vorgänge", „Vorgang 2026-0071".
   * Optional: on the pages of a Vorgang the frame above the page names it
   * (design 2026-10-22 §5), so a head there has nothing to label and draws
   * no `<p>` for it.
   */
  label?: ReactNode;
  title: string;
  /** Takes the focus when the screen is opened (route focus, UX ruling 12). */
  headingRef?: Ref<HTMLHeadingElement>;
  /** Under the title, in the left column: stage path, meta line, notices. */
  children?: ReactNode;
  /** Right column, aligned to the bottom of the title block (mockup A). */
  actions?: ReactNode;
  /** A class of the screen's own on the `h1` — the record's names its title
   * for the page turn from the list (design 2026-10-24 §7). */
  titleClassName?: string;
  /**
   * The title of a place — Start, Vorgänge, a new outline, a dead end — in
   * the one rule places take (UX-Gutachten 2, B-13): the display face at
   * 28 px behind the red marker, no kicker.
   */
  place?: boolean;
}

/**
 * One head for every screen of the repository, the calculation's own
 * (UX ruling 2, F-9): tracked-caps label (where it has something to say,
 * behind the red mesh marker of „Stahl", design 2026-10-24 §8.6), the
 * title in the display face at 700 in ink — 800 is the „K60" of the mark
 * (CI §4.1) — and the actions at the bottom right. The title is focusable
 * (`tabIndex={-1}`) so a screen can put a person down on it when it is opened;
 * the page's `h1` on its own, a section title under the Vorgang's head in a
 * Vorgang's frame (`PageTitle`, Task 9).
 */
export function ScreenHead({ label, title, headingRef, children, actions, titleClassName, place = false }: ScreenHeadProps) {
  return (
    <header className={styles.head}>
      <div className={styles.main}>
        {label !== undefined ? (
          <p className="label">
            {/* The mesh marker before the kicker (design 2026-10-24 §8.6) —
                the screen's title, so its one red one (Task 11, B-11). */}
            <span className="marke marke-titel" aria-hidden="true" />
            {label}
          </p>
        ) : null}
        <PageTitle
          headingRef={headingRef}
          tabIndex={-1}
          marked={label === undefined}
          className={[styles.title, place ? styles.placeTitle : null, styles.fit, titleClassName].filter(Boolean).join(" ")}
        >
          {title}
        </PageTitle>
        {children}
      </div>
      {actions !== undefined ? <div className={styles.actions}>{actions}</div> : null}
    </header>
  );
}
